import { SelectOptionType, TFolder } from "../../../../types/types";

const normalizeFolderName = (name: string) => name.trim().toUpperCase();

const getComparableFolderName = (name: string) =>
  normalizeFolderName(name).replace(/[^A-Z]/g, "");

const hasFolderAttribute = (folder: TFolder, attribute: string) => {
  return Array.isArray(folder.attributes)
    ? folder.attributes.some(
        (item) => item.toUpperCase() === attribute.toUpperCase(),
      )
    : false;
};

const isInboxFolder = (folder: TFolder) =>
  getComparableFolderName(folder.name) === "INBOX" ||
  hasFolderAttribute(folder, "\\Inbox");

const isSentFolder = (folder: TFolder) =>
  getComparableFolderName(folder.name).startsWith("SENT") ||
  hasFolderAttribute(folder, "\\Sent");

const getInboxFolder = (folders?: TFolder[]) => folders?.find(isInboxFolder);

const getSentFolder = (folders?: TFolder[]) => folders?.find(isSentFolder);

const mapFolderToOption = (
  folder: TFolder,
  label: string,
): SelectOptionType => ({
  label,
  value: normalizeFolderName(folder.name),
  totalCount: folder.total_count || 0,
});

export const buildContactEmailFolderOptions = (
  folders?: TFolder[],
): SelectOptionType[] => {
  const inboxFolder = getInboxFolder(folders);
  const sentFolder = getSentFolder(folders);

  return [
    ...(inboxFolder ? [mapFolderToOption(inboxFolder, "Inbox")] : []),
    ...(sentFolder ? [mapFolderToOption(sentFolder, "Sent")] : []),
  ];
};

export const getDefaultContactEmailFolderOption = (folders?: TFolder[]) => {
  const inboxFolder = getInboxFolder(folders);

  return inboxFolder ? mapFolderToOption(inboxFolder, "Inbox") : null;
};

export const getEmailRequestErrorText = (error: unknown) => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;

    return typeof data === "string" ? data : "";
  }

  return "";
};

const NOT_CONNECTED_TEXT = "try authorizing again";

/**
 * The client Email tab when loading failed for another reason than "mailbox
 * not connected" (that one shows the Connect screen): the server's sentence
 * instead of "No emails yet" (QA batch 19, PROF-036: the tab was blank).
 */
export const contactEmailsErrorState = (
  errorText: string,
): { title: string; description: string } | null => {
  const text = (errorText || "").trim();
  if (!text || text.toLowerCase().includes(NOT_CONNECTED_TEXT)) return null;
  const readable = text.length <= 300 && !/exception|\n\s+at /i.test(text);
  return {
    title: "Couldn't load emails",
    description: readable
      ? text
      : "We couldn't load emails from your mailbox right now. Please try again in a minute.",
  };
};
